import React, { useEffect, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker, Line, ZoomableGroup } from "react-simple-maps";

// Public-domain world map TopoJSON (Natural Earth 110m, country boundaries dissolved per-country)
const TOPO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json";

// City lat/lon
const HUB = { name: "Dubai", lonlat: [55.27, 25.20] };
const ORIGINS = [
  { name: "Kenya",     lonlat: [37.9,  -0.0] },
  { name: "Tanzania",  lonlat: [34.9,  -6.4] },
  { name: "Ethiopia",  lonlat: [40.5,   9.1] },
  { name: "Uganda",    lonlat: [32.3,   1.4] },
  { name: "India",     lonlat: [78.9,  20.6] },
  { name: "Pakistan",  lonlat: [69.4,  30.4] },
  { name: "Brazil",    lonlat: [-51.9, -14.2] },
  { name: "Argentina", lonlat: [-63.6, -38.4] },
];
const MARKETS = [
  { name: "UK",        lonlat: [-2.0,  54.0] },
  { name: "Germany",   lonlat: [10.4,  51.2] },
  { name: "Japan",     lonlat: [138.3, 36.2] },
  { name: "Singapore", lonlat: [103.8,  1.4] },
];

export default function TradeCorridorMap() {
  const wrapperRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setInView(true),
      { threshold: 0.1 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      setPhase(((t - start) / 3500) % 1);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  // Compute pulse position along great-circle for animated travel (linear interp suffices visually)
  const pulsePos = (from, to, t) => [
    from[0] + (to[0] - from[0]) * t,
    from[1] + (to[1] - from[1]) * t,
  ];

  return (
    <div ref={wrapperRef} className="relative w-full bg-white border border-stone-200 rounded-2xl p-2 sm:p-4 overflow-hidden">
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: 165 }}
        style={{ width: "100%", height: "auto" }}
      >
        <ZoomableGroup center={[15, 15]} zoom={1} disablePanning>
          <Geographies geography={TOPO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: {
                      fill: "#F4E8E9",
                      stroke: "#E5C5C8",
                      strokeWidth: 0.5,
                      outline: "none",
                    },
                    hover: { fill: "#F4E8E9", outline: "none" },
                    pressed: { fill: "#F4E8E9", outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>

          {/* Glow layer under arcs */}
          {[...ORIGINS, ...MARKETS].map((p) => (
            <Line
              key={`glow-${p.name}`}
              from={p.lonlat}
              to={HUB.lonlat}
              stroke="#A8323F"
              strokeOpacity={0.25}
              strokeWidth={5}
              strokeLinecap="round"
            />
          ))}

          {/* Origin -> Hub (solid red) */}
          {ORIGINS.map((p) => (
            <Line
              key={`o-${p.name}`}
              from={p.lonlat}
              to={HUB.lonlat}
              stroke="#6B0F1A"
              strokeWidth={1.6}
              strokeLinecap="round"
            />
          ))}

          {/* Hub -> Market (dashed blue) */}
          {MARKETS.map((p) => (
            <Line
              key={`m-${p.name}`}
              from={HUB.lonlat}
              to={p.lonlat}
              stroke="#1F4DB7"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeDasharray="5,4"
            />
          ))}

          {/* Animated travel pulses on origin arcs */}
          {inView && ORIGINS.map((p, i) => {
            const t = (phase + i / ORIGINS.length) % 1;
            const pos = pulsePos(p.lonlat, HUB.lonlat, t);
            return (
              <Marker key={`pulse-${p.name}`} coordinates={pos}>
                <circle r={3} fill="#A8323F" opacity={0.95} />
              </Marker>
            );
          })}

          {/* Origins markers */}
          {ORIGINS.map((p) => (
            <Marker key={p.name} coordinates={p.lonlat}>
              <circle r={3.5} fill="#0E7C4A" stroke="#fff" strokeWidth={1.5} />
              <text
                textAnchor="start"
                x={6}
                y={-6}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  fill: "#1F1F1F",
                  paintOrder: "stroke",
                  stroke: "#FFFFFF",
                  strokeWidth: 3,
                  strokeLinejoin: "round",
                }}
              >
                {p.name}
              </text>
            </Marker>
          ))}

          {/* Markets markers */}
          {MARKETS.map((p) => (
            <Marker key={p.name} coordinates={p.lonlat}>
              <circle r={3.5} fill="#1F4DB7" stroke="#fff" strokeWidth={1.5} />
              <text
                textAnchor="start"
                x={6}
                y={-6}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  fill: "#1F1F1F",
                  paintOrder: "stroke",
                  stroke: "#FFFFFF",
                  strokeWidth: 3,
                  strokeLinejoin: "round",
                }}
              >
                {p.name}
              </text>
            </Marker>
          ))}

          {/* Hub */}
          <Marker coordinates={HUB.lonlat}>
            <circle r={20} fill="#6B0F1A" opacity={0}>
              <animate attributeName="r" values="6;28;6" dur="2.6s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.55;0;0.55" dur="2.6s" repeatCount="indefinite" />
            </circle>
            <circle r={6} fill="#6B0F1A" stroke="#fff" strokeWidth={2} />
            <text
              textAnchor="start"
              x={9}
              y={-7}
              style={{
                fontSize: 12,
                fontWeight: 800,
                fill: "#1F1F1F",
                paintOrder: "stroke",
                stroke: "#FFFFFF",
                strokeWidth: 3.5,
                strokeLinejoin: "round",
              }}
            >
              Dubai
            </text>
          </Marker>
        </ZoomableGroup>
      </ComposableMap>

      <div className="flex flex-wrap gap-3 px-2 pt-3 pb-1 text-[11px] sm:text-xs text-stone-600 font-semibold">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-700" />Origins</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#6B0F1A]" />Hub</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-blue-700" />Markets</span>
      </div>
    </div>
  );
}
